import {BaseElementAsync, OSkinableInit} from "back/commons/basis";
import {IApp, IAppCtx} from "back/core/appFrame";
import {JSX} from "lib/commons/xml/dom";
import {IView, IViewsContainer, OViewVisitOptions} from "lib/commons/views";
import {IReg, IRegPointer, IUiEnv, REG} from "lib/commons/registry";
import {DOMSH} from "lib/commons/xml/domsh";
import {BasicUniverse, IUniverseEnv} from "lib/core/universe";
import {ILastDatasBuilder, JLastDatas, LASTDATAS} from "lib/commons/lastDatas";
import {BlocksCollapsable, JLDBlocksCollapsable, OBlocksCollapsableInit} from "back/commons/widgets/blocks";
import {Area} from "lib/commons/areas";
import {AppHeader} from "back/core/widgets/appHeader";
import {JobsMgr, OJobsMgrInit} from "back/core/dialogs/jobsMgr";
import "back/core/apps/apps_Perms";
import {JJobsAppDef} from "back/core/plugins/jobsPlg";
import {AccelKeyMgr, Action, IAccelKeyMgrPointer, IActionToggle} from "lib/commons/actions";
import {IExecutorInUniverse} from "lib/core/executor";


export interface JLDJobsApp extends JLastDatas {
	/** Rafraichissement auto des zones ouvertes */
	autoRefresh?: boolean
}

export interface JobsApp extends BaseElementAsync {
	initialize(init: OJobsAppInit & IAppCtx<IUniverseEnv>): this;
}

export interface OJobsAppInit extends OSkinableInit {
	/** nombre d'entrées de log maximal ouvert par défaut.
	 * 5 si non défini */
	maxOpenEntriesByDefault?: number,
	blocksCollapsableInit?: OBlocksCollapsableInit<any>,
	/** Rafraichissement auto des zones ouvertes */
	autoRefresh?: boolean,
	/** Gap de rafraichissement auto en seconde */
	autoRefreshInterval?: number,
}

export class JobsApp extends BaseElementAsync implements IApp<IUniverseEnv>, IViewsContainer, ILastDatasBuilder, IAccelKeyMgrPointer<IRegPointer<IUniverseEnv & IUiEnv>> {

	reg: IReg<IUniverseEnv & IUiEnv>;

	appDef: JJobsAppDef;

	appHeader: AppHeader<JobsApp>;

	accelKeyMgr: AccelKeyMgr<IRegPointer<IUniverseEnv & IUiEnv>>;

	blocksCollapsable: BlocksCollapsable;

	params: OJobsAppInit & IAppCtx<IUniverseEnv>;

	/** Auto refresh des blocks */
	get autoRefresh(): boolean {return this.params.autoRefresh};

	set autoRefresh(val: boolean) {
		if (this.params.autoRefresh !== val) {
			this.params.autoRefresh = val;
			if (val)
				this.refreshAllBlocksFetch().then(() => {
					this._registerAutoRefreshHandler()
				});
			else
				clearTimeout(this._autoRefreshHandler);
			this._refreshui();
		}
	}

	protected async _initialize(init: OJobsAppInit & IAppCtx<IUniverseEnv>) {
		this.params = Object.assign(init, {
			maxOpenEntriesByDefault: 5,
			autoRefresh: true,
			autoRefreshInterval: 15,
		});
		this.appDef = init.appDef as JJobsAppDef;
		this.reg = this.findReg(init) as IReg<IUniverseEnv>;
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		const sr = this.shadowRoot;
		this.reg.installSkin('scroll/large', sr);
		this._initAndInstallSkin(this.localName, init);
		const showUniverses = this.appDef.jobsUniverse;

		this.initRegExtPoints();

		if (!this.accelKeyMgr) {
			this.accelKeyMgr = new AccelKeyMgr();
			this.addEventListener('keydown', function (this: JobsApp, ev: KeyboardEvent) {
				this.accelKeyMgr.handleKeyboardEvent(ev, this)
			});
		} else {
			this.accelKeyMgr.reset();
		}
		this.accelKeyMgr.initFromMapActions(this.reg.getListAsMap("accelkeys:jobsApp:global"));

		// # AppHeader
		this.appHeader = sr.appendChild(new AppHeader<JobsApp>().initialize({
			reg: this.reg,
			skinOver: "jobs-app/header",
			actionContext: this,
			mainActions: this.reg.getList("actions:jobsApp:bar:main"),
		}));
		this.setAttribute("label", "Traitements");
		this.appHeader.appendChild(<header><h1>Traitements</h1></header>);

		// # Blocks
		let universes = init.reg.getList("plg:jobs:universes") as (BasicUniverse & IExecutorInUniverse)[];
		universes = universes.filter((entry) => entry.executor && entry.reg.hasPerm("ui.apps.jobs.show") ? true : false);

		const LDkey = "blocks";
		let LD: JLDBlocksCollapsable = (init.lastDatas ? init.lastDatas[LDkey] : null) as JLDBlocksCollapsable;

		// Ouverture des blocks par défaut (si pas de LD)
		if (!LD && init.maxOpenEntriesByDefault) {
			LD = {open: []};
			for (var i = 0; i < universes.length; i++) {
				const univ = universes[i];
				if (i < init.maxOpenEntriesByDefault)
					LD.open.push(univ.getId())
				else
					break;
			}
		}

		this.blocksCollapsable = sr.appendChild(<BlocksCollapsable î={Object.assign({
			reg: this.reg,
			areasContext: {reg: this.reg},
			skinOver: "jobs-app/collapsableBlocks",
			lastDatasKey: LDkey,
			lastDatas: LD,
		} as OBlocksCollapsableInit<any>, init.blocksCollapsableInit)}/>) as BlocksCollapsable;

		universes.forEach((univ, pos) => {
			this.blocksCollapsable.addArea(
				new Area(univ.getId()).setLabel(univ.getName()).requireVisiblePerm("ui.apps.jobs.show").setBodyBuilder((ctx) =>
					<JobsMgr î={{reg: univ.reg, jobsTab: {skinOver: "jobs-app/mgr/tab"}} as OJobsMgrInit}/>
				),
				null,
				{
					reg: univ.reg,
					areaContext: {reg: univ.reg},
					parentBlockCtn: this.blocksCollapsable,
					locked: universes.length == 1 ? true : undefined,
				}
			)
		});

		// Auto refresh
		if (this.params.lastDatas && (this.params.lastDatas as JLDJobsApp).autoRefresh !== undefined)
			this.autoRefresh = (this.params.lastDatas as JLDJobsApp).autoRefresh;
		else this.autoRefresh = this.autoRefresh; //utile pour refresh UI
		if (this.autoRefresh)
			this._registerAutoRefreshHandler()
	}

	/** Refresh des composants UI */
	protected _refreshui() {
		this.appHeader.refresh();
	}

	/** Rafraichit tous els blocks ouverts */
	async refreshAllBlocksFetch(): Promise<void> {
		if (this.isConnected) {
			let selectedBlocks = this.blocksCollapsable.selectedBlocks;
			if (selectedBlocks) {
				Promise.all(selectedBlocks.map(async (block) => {
					let view = block.view as JobsMgr<any, any>;
					if (view)
						await view.fetchAll();
				}));
			}
		}
	}

	/** déclare le handler de refresh auto */
	private _registerAutoRefreshHandler() {
		if (this._autoRefreshHandler)
			clearTimeout(this._autoRefreshHandler);
		this._autoRefreshHandler = window.setTimeout(async () => {
			await this.refreshAllBlocksFetch();
			if (this.autoRefresh) this._registerAutoRefreshHandler();
		}, this.params.autoRefreshInterval * 1000);
	}

	private _autoRefreshHandler: number;

	/** Init des extPoints du registre de l'app. */
	protected initRegExtPoints() {
		const reg = this.reg;

		function addMainBarAction(action: Action<JobsApp>, accel?: string) {
			reg.addToList("actions:jobsApp:bar:main", action.getId(), 1, action);
			if (accel)
				reg.addToList("accelkeys:jobsApp:global", accel, 1, action);
		}

		//addMainBarAction(new RefreshAllBlocksAction(), "F5-ctrl");
		addMainBarAction(new AutoRefreshBtn());
	}

	updateAppDef(def: JJobsAppDef): boolean {
		if (this.appDef === def) return true; //chgt issu d'ici
		return false;
	}

	buildLastDatas(parentLastDatas: JLastDatas): void {
		if (this.params.lastDatas && (this.params.lastDatas as JLDJobsApp).autoRefresh !== undefined) {
			(parentLastDatas as JLDJobsApp).autoRefresh = (this.params.lastDatas as JLDJobsApp).autoRefresh;
		}
		LASTDATAS.buildLastDatas(parentLastDatas, this.blocksCollapsable, true);
	}

	visitViews(visitor: (view: IView) => any) {
		visitor(this.blocksCollapsable)
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return visitor(this.blocksCollapsable);
	}

	/** Retourne true si au moins un univers est visible dans cette app */
	static atLeastOneEntry(reg: IReg<any>): boolean {
		let universes = reg.getList("plg:jobs:universes") as (BasicUniverse & IExecutorInUniverse)[] || [];
		let result = false;
		for (const univ of universes) {
			if (univ.executor && univ.reg.hasPerm("ui.apps.jobs.show")) {
				result = true;
				break;
			}
		}
		return result;
	}
}


customElements.define('jobs-app', JobsApp);
REG.reg.registerSkin('jobs-app', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
		min-width: 0;
	}

	c-appheader {
		min-height: 2rem;
		z-index: 1;
		margin: 0 4px;
	}

	header {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		max-height: 5em;
		overflow: hidden;
		align-items: center;
		justify-content: center;
	  user-select: none;
  }

  h1 {
	  margin: .1em .5em;
	  font-size: 1em;
	  letter-spacing: 0.1em;
	  text-align: center;
  }

  c-blocks-collapsable {
	  flex: 1;
	  min-height: 0;
	  min-width: 0;
	  overflow: auto;
  }

`);

REG.reg.registerSkin('jobs-app/header', 1, /* language=CSS */ `
	:host > div {
		flex-direction: row-reverse;
	}
`);

REG.reg.registerSkin('jobs-app/collapsableBlocks', 1, /* language=CSS */ `
	jobs-mgr {
		flex: 1;
	}
`);


REG.reg.registerSkin("jobs-app/mgr/tab", 1, /* language=CSS */ `
	#head {
		background-color: var(--alt1-bgcolor);
	}
`);

/** Actions */

/** Rafraichissement de tous les fetchs des areas ouvertes */
class RefreshAllBlocksAction extends Action<JobsApp> {
	constructor() {
		super('refreshAllBlocks');
		this._label = "Rafraichir les zones affichées";
		this._icon = "/@skin@/commons/icons/refresh.svg";
	}

	execute(ctx: JobsApp, ev?: Event): any | "noPreventDefault" {
		ctx.refreshAllBlocksFetch();
	}
}

/** Rafraichissement de tous les fetchs des areas ouvertes */
class AutoRefreshBtn extends Action<JobsApp> {
	constructor() {
		super('autoRefresh');
		this._icon = "/@skin@/commons/icons/refresh.svg";
	}

	getLabel(ctx: JobsApp): string {
		return `Rafraichissement automatique des zones affichées (toutes les ${ctx.params.autoRefreshInterval} secondes)`;
	}

	isToggle(ctx: JobsApp): this is IActionToggle<JobsApp> {
		return true;
	}

	getIcon(ctx: JobsApp): string {
		return this.getDatas("toggle", ctx) ? "/@skin@/core/apps/autorefresh-on.svg" : "/@skin@/core/apps/autorefresh-off.svg";
	}

	getDatas(api: "toggle", ctx: JobsApp): boolean {
		return ctx.autoRefresh;
	}

	execute(ctx: JobsApp, ev?: Event): any | "noPreventDefault" {
		let currentStatus = this.getDatas("toggle", ctx);
		if (!ctx.params.lastDatas) ctx.params.lastDatas = {};
		(ctx.params.lastDatas as JLDJobsApp).autoRefresh = !currentStatus;
		ctx.autoRefresh = !currentStatus;
	}
}
